import { templatesWithUpload } from "@config";
import {
  useDeleteApproverMutation,
  useGetDevice,
  useGetDocumentApproversQuery,
  useGetDocuments,
  useGetDocumentsTemplates,
  useGetTasks,
  useGetUser,
  usePatchDocumentMutation,
  usePatchDocumentVersionMutation,
  usePostApproverMutation,
} from "@hooks";
import { Button } from "@mui/material";
import { DocumentStatus } from "@types";
import {
  getDependantTemplates,
  getDocTypeStatus,
  getDocVersionsWithStatus,
  getRecentlyUpdatedElementIds,
} from "@utils";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  BaseUser,
  Document,
  DocumentVersion,
  TEMPLATE_ID,
} from "../../../../../../stores/models";
import ApproversTabContentLoading from "../ApproversTabContentLoading";
import AssignApprovers from "./assignApprovers/AssignApprovers";
import DependencyWarning from "./dependencyWarning/DependencyWarning";
import DocMetadata from "./docMetadata/DocMetadata";
import { enableSaveButton } from "./helpers";
import RevisionSummary from "./revisionSummary/RevisionSummary";

const requiredNumberOfApprovers = 2;

type AuthorContentProps = {
  author: BaseUser;
  isDemoDevice: boolean;
  docId: string;
  selectedDocVersion: DocumentVersion;
  selectedDoc: Document;
  generatedDoc: string | undefined;
};

export type LocalData = {
  selectedUsers: Array<BaseUser | null>;
  revisionSummary: string;
  documentMetadata: {
    title?: Document["title"];
    regulatoryType?: Document["regulatoryType"];
    regulatoryFunctionalGroup?: Document["regulatoryFunctionalGroup"];
  };
};

export const AuthorContent = ({
  author,
  isDemoDevice,
  docId,
  selectedDocVersion,
  selectedDoc,
  generatedDoc,
}: AuthorContentProps) => {
  const { deviceId = "", orgId = "", templateKey = "" } = useParams();

  const { data: user } = useGetUser();
  const { data: templatesConfig } = useGetDocumentsTemplates({
    orgId,
    deviceId,
  });
  const { data: documents } = useGetDocuments(orgId, deviceId, templatesConfig);
  const { data: device } = useGetDevice({ orgId, deviceId });

  const { data: approvers } = useGetDocumentApproversQuery({
    orgId,
    documentId: selectedDocVersion.documentId,
    versionId: selectedDocVersion.id,
  });
  const { data: tasks } = useGetTasks(orgId, deviceId);

  if (
    !device ||
    !documents ||
    !templateKey ||
    !approvers ||
    !user ||
    !templatesConfig ||
    !tasks
  ) {
    return <ApproversTabContentLoading />;
  }

  const [enableApproversEditing, setEnableApproversEditing] = useState(true);

  const [saving, setSaving] = useState(false);

  const [localData, setLocalData] = useState<LocalData>({
    selectedUsers: selectedDocVersion.documentVersionApprover.map(
      (approver) => approver.user
    ),
    revisionSummary: selectedDocVersion.revisionSummary,
    documentMetadata: {
      title: selectedDoc.title || "",
      regulatoryType: selectedDoc.regulatoryType,
      regulatoryFunctionalGroup: selectedDoc.regulatoryFunctionalGroup,
    },
  });

  useEffect(() => {
    if (approvers) {
      setLocalData((prev) => ({
        ...prev,
        selectedUsers: approvers.map((approver) => approver.user),
      }));
    }

    if (approvers?.length === requiredNumberOfApprovers) {
      setEnableApproversEditing(false);
    }
  }, [approvers]);

  const approverMutation = usePostApproverMutation({
    orgId,
    documentId: selectedDocVersion.documentId,
    versionId: selectedDocVersion.id,
    deviceId,
    approvers: localData.selectedUsers,
  });

  const removeApproverMutation = useDeleteApproverMutation({
    orgId,
    documentId: selectedDocVersion.documentId,
    versionId: selectedDocVersion.id,
    deviceId,
  });

  const { mutate: patchDocument } = usePatchDocumentMutation();

  const patchDocumentMutation = usePatchDocumentVersionMutation();

  const secondToLastVersion = getDocVersionsWithStatus({
    document: selectedDoc,
    user,
    documents,
    templateKey: selectedDoc.templateKey as TEMPLATE_ID,
    orgId,
    device,
    templatesConfig,
    tasks,
  })[1];

  const recentlyUpdatedElementIds = getRecentlyUpdatedElementIds(
    selectedDocVersion,
    secondToLastVersion
  );

  const dependentDocs = getDependantTemplates(
    recentlyUpdatedElementIds,
    templatesConfig
  ).filter(([template, _]) => {
    const status = getDocTypeStatus({
      documents,
      templateKey: template as TEMPLATE_ID,
      user,
      orgId,
      device,
      templatesConfig,
      tasks,
    });

    const isUnapprovedOrLive =
      status === DocumentStatus.UNAPPROVED || status === DocumentStatus.LIVE;
    const isNotSelf = template !== templateKey;

    return isUnapprovedOrLive && isNotSelf;
  });

  const handleSave = async () => {
    setEnableApproversEditing(false);
    setSaving(true);

    /** Metadata */
    if (showMetadataSection) {
      Object.entries(localData.documentMetadata).forEach(([key, value]) => {
        const isMetadataKeyUpdated =
          value && value !== selectedDoc[key as keyof typeof selectedDoc];

        if (isMetadataKeyUpdated) {
          patchDocument({
            orgId,
            deviceId,
            docId: selectedDoc.id,
            data: {
              [key]: value,
            },
          });
        }
      });
    }

    /** Approvers */
    const newUsers = localData.selectedUsers.filter(
      (s) => !approvers.find((a) => a.user.id === s?.id)
    );
    const removedUsers = approvers.filter(
      (a) => !localData.selectedUsers.find((s) => s?.id === a.user.id)
    );

    // For each approver that got removed, remove the approver from the backend and for each approver that got added, add the approver to the backend
    const deletedUsers = await Promise.all(
      removedUsers.map(async (approver) => {
        const { status } = await removeApproverMutation.mutateAsync({
          approverId: approver.id,
        });
        return status;
      })
    );

    if (deletedUsers.every((result) => result === 200)) {
      await Promise.all(
        newUsers
          .filter((user) => user !== null)
          .map(async (user) => {
            await approverMutation.mutateAsync({
              userId: user.id,
            });
          })
      );
    }

    /** Snapshot Check */
    const versionNeedsSnapshot = !templatesWithUpload(templatesConfig).includes(
      selectedDoc.templateKey as TEMPLATE_ID
    );

    let snapshot = selectedDocVersion.snapshot;

    if (versionNeedsSnapshot && !snapshot) {
      if (!generatedDoc) {
        throw new Error(
          "To submit this revision the generated document is required but missing."
        );
      }
      snapshot = generatedDoc;
    }

    if (!snapshot && versionNeedsSnapshot) {
      throw new Error("Snapshot needs to be defined but is missing.");
    }

    /** Revision Summary */
    if (localData.revisionSummary !== selectedDocVersion.revisionSummary) {
      await patchDocumentMutation.mutateAsync({
        orgId,
        docId: selectedDocVersion.documentId,
        docVersionId: selectedDocVersion.id,
        deviceId,
        data: {
          revisionSummary: localData.revisionSummary,
          ...(versionNeedsSnapshot &&
          snapshot &&
          snapshot !== selectedDocVersion.snapshot
            ? { snapshot }
            : {}),
        },
      });
    }

    setSaving(false);
  };

  const [seeMore, setSeeMore] = useState<boolean>(false);
  const [agreement, setAgreement] = useState<boolean>(false);

  const showMetadataSection =
    (!selectedDoc.title ||
      !selectedDoc.regulatoryType ||
      !selectedDoc.regulatoryFunctionalGroup) &&
    selectedDoc.templateKey === TEMPLATE_ID.USER_UPLOAD;

  const saveEnabled = enableSaveButton(
    selectedDoc,
    localData,
    selectedDocVersion,
    dependentDocs,
    agreement,
    approvers,
    saving,
    showMetadataSection
  );

  return (
    <>
      <div
        className={`flex flex-col gap-4 mb-4 overflow-auto ${
          seeMore ? "overflow-scroll max-h-[70vh]" : ""
        }`}
      >
        {showMetadataSection && (
          <DocMetadata
            docId={docId}
            localData={localData}
            setLocalData={setLocalData}
          />
        )}

        <AssignApprovers
          localData={localData}
          setLocalData={setLocalData}
          enableApproversEditing={enableApproversEditing}
          setEnableApproversEditing={setEnableApproversEditing}
          organizationId={orgId}
          userId={author.id}
          savedApprovers={approvers}
          saving={saving}
          isDemoDevice={isDemoDevice}
        />

        <RevisionSummary localData={localData} setLocalData={setLocalData} />

        <DependencyWarning
          dependentDocs={dependentDocs}
          setAgreement={setAgreement}
          agreement={agreement}
          seeMore={seeMore}
          setSeeMore={setSeeMore}
        />
      </div>
      <Button
        variant="contained"
        disabled={!saveEnabled}
        onClick={handleSave}
        size="large"
      >
        Submit
      </Button>
    </>
  );
};

export default AuthorContent;
